// Theme-side freebie ENTITLEMENT OBSERVER — Head 2 ("belt") of the freebie
// leak fix (PLAN-2026-07-19-freebie-entitlement-fix.md, Asana
// 1215689041980756). The server-side automatic BXGY (Head 1) is the
// authoritative gate; this is the belt-and-suspenders reconcile that catches
// a cart still carrying a freebie/flow line (or a stale freebie
// discount_code cookie) after the qualifying box has been removed — e.g. the
// bounce-out path (enter flow -> select freebie -> remove the box -> keep
// shopping).
//
// FAIL-SAFE BY DESIGN: window.N2T_ENTITLEMENT.qualifyingProductIds is
// rendered (snippets/n2t-entitlement-config.liquid) from the `plan-boxes`
// smart collection, which does NOT exist yet as of 2026-07-19 (Admin phase
// not done). Until it does, qualifyingProductIds is empty and this observer
// disables itself entirely at bootstrap — no listeners attached, no cart
// reads, no removals. Ships inert ahead of the Admin phase.

// Node-test guard, consistent with assets/seafood-plan-single-flow.js: lets
// vitest require() this file to exercise its pure/injectable helpers without
// it wiring real DOM listeners (DOMContentLoaded / pageshow / the
// CartResilience mutation hook) against the shared jsdom window for the
// whole test file.
var __N2T_IS_NODE_TEST =
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null;

var ENTITLEMENT_DISCOUNT_COOKIE_NAME = 'discount_code';
var ENTITLEMENT_DEBOUNCE_MS = 300;

function getEntitlementCookieValue(name) {
  var value = '; ' + document.cookie;
  var parts = value.split('; ' + name + '=');
  if (parts.length === 2) return parts.pop().split(';').shift();
}

// Copies DiscountManager's cookie-clear mechanics (path=/, expiry in the
// past — see DiscountManager.setDiscountCookie/clearDiscountCookie). Callers
// MUST only invoke this after confirming the current cookie value is in the
// freebieCodes allowlist — this function itself does not check; it
// unconditionally clears, same as DiscountManager's own clear. Never used to
// touch an affiliate/marketing code: see reconcileEntitlement's allowlist gate.
function clearEntitlementDiscountCookie() {
  document.cookie =
    ENTITLEMENT_DISCOUNT_COOKIE_NAME + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
}

// Refresh the cart after an entitlement strip without treating cleanup as
// an add-to-cart. CartDrawer.renderContents opens the drawer and only updates
// its inner markup; it leaves the cart page/footer and outer empty state stale.
// Keep this helper's existing name, but quietly sync every present surface from
// one fresh section response. Validate all fragments before changing any DOM so
// an unavailable section cannot leave totals on different cart versions.
async function refreshEntitlementCartDrawer(deps) {
  var d = deps || {};
  var doc = d.document || (typeof document !== 'undefined' ? document : undefined);
  var win = d.window || (typeof window !== 'undefined' ? window : undefined);
  var doFetch = d.fetch || (typeof fetch !== 'undefined' ? fetch : undefined);

  if (!doc || !win || !doFetch) return { status: 'skipped', reason: 'refresh-unavailable' };

  function failed(reason, section) {
    var message = (win.cartStrings && win.cartStrings.error) ||
      'Please refresh this page to see your updated cart.';
    ['cart-errors', 'CartDrawer-CartErrors'].forEach(function (id) {
      var error = doc.getElementById(id);
      if (error) error.textContent = message;
    });
    return { status: 'failed', reason: reason, section: section || null };
  }

  var targets = [];
  if (doc.querySelector('cart-drawer')) {
    targets.push({
      section: 'cart-drawer', selector: 'cart-drawer .drawer__inner',
      emptySelector: 'cart-drawer',
    });
  }
  if (doc.getElementById('cart-icon-bubble')) {
    targets.push({ section: 'cart-icon-bubble', selector: '#cart-icon-bubble', sourceSelector: '.shopify-section' });
  }
  var items = doc.getElementById('main-cart-items');
  if (items) {
    targets.push({
      section: items.getAttribute('data-id') || 'main-cart-items',
      selector: 'cart-items', emptySelector: 'cart-items',
    });
  }
  var footer = doc.getElementById('main-cart-footer');
  if (footer) {
    targets.push({
      section: footer.getAttribute('data-id') || 'main-cart-footer',
      selector: '#main-cart-footer', emptySelector: '#main-cart-footer',
    });
  }
  if (targets.length === 0) return { status: 'skipped', reason: 'no-cart-surfaces' };

  var Parser = win.DOMParser || (doc.defaultView && doc.defaultView.DOMParser);
  if (!Parser) return failed('parser-unavailable');
  var root = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
  var cartUrl = (win.routes && win.routes.cart_url) || root + 'cart';
  var sectionIds = targets.map(function (target) { return target.section; });

  try {
    // /cart negotiates ordinary cart JSON when Accept is application/json,
    // even with ?sections=. Keep the default Accept to receive section HTML.
    var response = await doFetch(cartUrl + '?sections=' + encodeURIComponent(sectionIds.join(',')), {
      cache: 'no-store',
    });
    if (!response || !response.ok) return failed('http-error');
    var sections = await response.json();
    var updates = [];

    for (var i = 0; i < targets.length; i++) {
      var target = targets[i];
      var markup = sections && sections[target.section];
      if (typeof markup !== 'string' || markup.trim().length === 0) {
        return failed('section-unavailable', target.section);
      }
      var html = new Parser().parseFromString(markup, 'text/html');
      var source = html.querySelector(target.sourceSelector || target.selector);
      var element = doc.querySelector(target.selector);
      var sourceState = target.emptySelector && html.querySelector(target.emptySelector);
      var stateElement = target.emptySelector && doc.querySelector(target.emptySelector);
      if (!source || !element || (target.emptySelector && (!sourceState || !stateElement))) {
        return failed('section-invalid', target.section);
      }
      updates.push({
        element: element, html: source.innerHTML, stateElement: stateElement,
        empty: sourceState ? sourceState.classList.contains('is-empty') : false,
        elementEmpty: source.classList.contains('is-empty'),
      });
    }

    var drawer = doc.querySelector('cart-drawer');
    var drawerWasOpen = drawer && drawer.classList.contains('active');
    var focusedId = drawerWasOpen && drawer.contains(doc.activeElement) ? doc.activeElement.id : '';
    updates.forEach(function (update) {
      // Capture at replacement time: a mutation may report an error while the
      // sections request is pending. Line numbers can shift after cleanup, so
      // keep line messages only for surviving keys on the same cart surface.
      var globalErrors = [];
      update.element.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach(function (error) {
        if (error.textContent.trim()) globalErrors.push({ id: error.id, text: error.textContent });
      });
      var lineErrors = new Map();
      update.element.querySelectorAll('.cart-item').forEach(function (item) {
        var keyed = item.querySelector('[data-key]');
        var error = item.querySelector('.cart-item__error-text');
        if (keyed && error && error.textContent.trim()) {
          lineErrors.set(keyed.getAttribute('data-key'), error.textContent);
        }
      });
      update.element.innerHTML = update.html;
      globalErrors.forEach(function (saved) {
        var error = doc.getElementById(saved.id);
        if (error && !error.textContent.trim()) error.textContent = saved.text;
      });
      update.element.querySelectorAll('.cart-item').forEach(function (item) {
        var keyed = item.querySelector('[data-key]');
        var error = item.querySelector('.cart-item__error-text');
        var message = keyed && lineErrors.get(keyed.getAttribute('data-key'));
        if (message && error && !error.textContent.trim()) error.textContent = message;
      });
      update.element.classList.toggle('is-empty', update.elementEmpty);
      if (update.stateElement) update.stateElement.classList.toggle('is-empty', update.empty);
    });
    // Retain the drawer element and its overlay listener, plus active/animate
    // classes. Cleanup must neither open a closed drawer nor close an open one.
    // An open drawer's trap cached nodes that innerHTML just disconnected.
    // Rebuild it from the live content; never call open() or trap a closed drawer.
    if (drawerWasOpen && drawer.classList.contains('active')) {
      var container = drawer.classList.contains('is-empty')
        ? drawer.querySelector('.drawer__inner-empty') : drawer.querySelector('#CartDrawer');
      var focusElement = focusedId && doc.getElementById(focusedId);
      if (!focusElement || !container || !container.contains(focusElement) || focusElement.disabled) {
        focusElement = container && (container.querySelector('.drawer__close') ||
          container.querySelector('a[href], button:enabled, input:enabled'));
      }
      var retrapFocus = d.trapFocus || win.trapFocus;
      if (container && focusElement && typeof retrapFocus === 'function') retrapFocus(container, focusElement);
    }
    return { status: 'refreshed', sections: sectionIds };
  } catch (error) {
    return failed('request-failed');
  }
}

// Reads the live cart, decides via NttUtils.decideEntitlementAction, and on a
// 'strip' decision removes the orphaned freebie/flow line(s) + clears the
// discount_code cookie ONLY when its current value is a known freebie code.
// Injectable deps ({ NttUtils, document, window, fetch }) mirror the pattern
// used throughout NttUtils.js/seafood-plan-single-flow.js for vitest.
//
// Empty flow-line spec ({variantIds: [], productHandles: []}) is deliberate:
// NttUtils.collectFlowLineKeys (which removeFlowLines uses) matches TAGGED
// lines (`_freebie` / `_n2t_flow` properties) independently of the spec's
// variantIds/productHandles — those fields only extend coverage to untagged
// legacy lines from pre-hotfix code. decideEntitlementAction's own 'strip'
// trigger already requires either a tagged line or a known freebie cookie
// (see NttUtils.js), so no variant/handle list is needed here.
function reconcileEntitlement(config, deps) {
  var d = deps || {};
  var nttUtils = d.NttUtils || (typeof window !== 'undefined' && window.NttUtils);
  if (!nttUtils || typeof nttUtils.getCart !== 'function') {
    return Promise.resolve({ action: 'none', reason: 'ntt-utils-unavailable' });
  }

  return nttUtils.getCart().then(function (cart) {
    // The free sockeye (Black Friday 2026) comes first, on its own: with the offer
    // configured, the cart gets exactly one free line on the plan matching a qualifying
    // subscription (added from any page, e.g. a product page Subscribe) and none
    // otherwise. Without it (offer product not published), stray offer lines are only
    // removed or capped. Then freebies reconcile as before.
    var plan =
      config.offer && typeof nttUtils.planOfferChanges === 'function'
        ? nttUtils.planOfferChanges(cart, config.offer)
        : {
            updates:
              typeof nttUtils.offerLineUpdates === 'function'
                ? nttUtils.offerLineUpdates(cart, config.qualifyingProductIds)
                : null,
            add: null,
          };
    if ((!plan.updates && !plan.add) || typeof nttUtils.fetchWithRetry !== 'function') return decideAndStrip(cart);

    var root = window.Shopify.routes.root;
    var latest = cart;
    var step = Promise.resolve(true);
    if (plan.updates) {
      step = nttUtils
        .fetchWithRetry(
          root + 'cart/update.js',
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: plan.updates }) },
          { retries: 2, backoffMs: 300, step: 'cart-offer-remove', bodyOptional: true },
        )
        .then(function (response) {
          if (!response || !response.ok) return false;
          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (updated) {
              if (updated && updated.items) latest = updated;
              return true;
            });
        });
    }
    if (plan.add && typeof nttUtils.addItemsToCart === 'function') {
      step = step.then(function (ok) {
        if (!ok) return false;
        // The guarded add seam (never a raw POST: an add increments).
        return nttUtils
          .addItemsToCart([plan.add], { step: 'cart-offer-add' })
          .then(function () {
            return nttUtils.getCart().then(
              function (fresh) {
                if (fresh && fresh.items) latest = fresh;
                return true;
              },
              function () {
                return true;
              },
            );
          });
      });
    }
    return step.then(
      function (changed) {
        if (!changed) return decideAndStrip(latest);
        return refreshEntitlementCartDrawer(d).then(function () {
          return decideAndStrip(latest);
        });
      },
      function () {
        return decideAndStrip(latest);
      },
    );
  });

  function decideAndStrip(cart) {
    // Freebie enforcement stays off until plan-boxes products exist (the offer alone
    // can switch the observer on).
    if (!config.qualifyingProductIds || config.qualifyingProductIds.length === 0) {
      return { action: 'none', reason: 'no-qualifying-products' };
    }
    var decision = nttUtils.decideEntitlementAction(cart, {
      qualifyingProductIds: config.qualifyingProductIds,
      freebieCodes: config.freebieCodes,
      discountCookieValue: getEntitlementCookieValue(ENTITLEMENT_DISCOUNT_COOKIE_NAME),
    });

    if (decision.action !== 'strip') return decision;

    return nttUtils
      .removeFlowLines(cart, { variantIds: [], productHandles: [] })
      .then(function () {
        var cookieValue = getEntitlementCookieValue(ENTITLEMENT_DISCOUNT_COOKIE_NAME);
        if (cookieValue && config.freebieCodes.indexOf(cookieValue) !== -1) {
          clearEntitlementDiscountCookie();
        }
        return refreshEntitlementCartDrawer(d);
      })
      .then(function (refresh) {
        return Object.assign({}, decision, { refresh: refresh });
      });
  }
}

// Single-flight + debounced scheduler around reconcileEntitlement. A factory
// (not a module-level singleton) so tests get isolated running/timer state
// per instance instead of sharing it across test cases.
//
// No-loop guarantee: reconcileEntitlement's own network calls
// (NttUtils.getCart -> fetchWithRetry, NttUtils.removeFlowLines ->
// fetchWithRetry, refreshEntitlementCartDrawer -> plain fetch) never go
// through window.CartResilience.cartFetch — the only place that fires the
// mutation-listener hook this scheduler is wired to (see
// assets/cart-resilience.js). Structurally, a reconcile run cannot re-trigger
// itself. The single-flight `running` flag + debounce are defense-in-depth
// for the case where several triggers (DOMContentLoaded, pageshow, a rapid
// cart mutation) fire close together.
function createEntitlementScheduler(config, deps) {
  var running = false;
  var rerun = false;
  var timer = null;
  var current = null;

  // A trigger that lands mid-run is kept, not dropped: the cart may have changed under
  // the run (e.g. the qualifying subscription removed while the sockeye add was in
  // flight), so one more pass runs once this one finishes.
  function runNow() {
    if (running) {
      rerun = true;
      return Promise.resolve({ action: 'none', reason: 'already-running' });
    }
    running = true;
    current = reconcileEntitlement(config, deps).finally(function () {
      running = false;
      if (rerun) {
        rerun = false;
        schedule();
      }
    });
    return current;
  }

  // For a caller about to leave the page (product-form's direct checkout): waits out a
  // run already in flight, which may have read the cart before the caller's add, then
  // runs a fresh pass at once instead of the debounced follow-up.
  function runFresh() {
    if (!running) return runNow();
    return current
      .catch(function () {})
      .then(function () {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        rerun = false;
        return runFresh();
      });
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      runNow();
    }, ENTITLEMENT_DEBOUNCE_MS);
  }

  return { runNow: runNow, runFresh: runFresh, schedule: schedule, isRunning: function () { return running; } };
}

if (!__N2T_IS_NODE_TEST) {
  (function bootstrapEntitlementObserver() {
    // Never run on the Skio app-proxy Build-a-Box page — theme JS can't
    // meaningfully reach its cart state there anyway, and this observer's job
    // is the direct-purchase/theme-cart entry points.
    if (
      window.location &&
      window.location.pathname &&
      window.location.pathname.indexOf('/apps/') === 0
    ) {
      return;
    }

    var entitlement = window.N2T_ENTITLEMENT;
    var qualifyingProductIds = (entitlement && entitlement.qualifyingProductIds) || [];
    var offer = (entitlement && entitlement.offer) || null;
    if (qualifyingProductIds.length === 0 && !offer) {
      // FAIL-SAFE: nothing configured to enforce against yet (plan-boxes
      // collection doesn't exist, no published offer) — do nothing at all.
      return;
    }

    var config = {
      qualifyingProductIds: qualifyingProductIds,
      freebieCodes: (entitlement && entitlement.freebieCodes) || [],
      offer: offer,
    };

    var scheduler = createEntitlementScheduler(config);
    // product-form.js calls this before its direct-to-checkout redirect (cart_type
    // 'page' renders no drawer, so no cartUpdate is published there).
    window.N2TCartEntitlement = { reconcileNow: scheduler.runFresh };

    document.addEventListener('DOMContentLoaded', scheduler.schedule);

    // bfcache guard — mirrors the pageshow pattern already in
    // assets/seafood-plan-single-flow.js (checks event.persisted); reconciles
    // instead of forcing a full page reload.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) scheduler.schedule();
    });

    // Product-page adds (product-form.js) don't go through CartResilience; they
    // publish cartUpdate, which is how a product page Subscribe gets its free sockeye.
    // pubsub.js loads after this file, so subscribe once the page has parsed.
    document.addEventListener('DOMContentLoaded', function () {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          scheduler.schedule();
        });
      }
    });

    if (window.CartResilience && typeof window.CartResilience.onCartMutation === 'function') {
      window.CartResilience.onCartMutation(function () {
        scheduler.schedule();
      });
    }
  })();
}

// Node-only test export. Skipped under <script> in the theme (process undefined).
if (
  typeof module === 'object' && module !== null &&
  typeof module.exports === 'object' &&
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null
) {
  module.exports = {
    getEntitlementCookieValue: getEntitlementCookieValue,
    clearEntitlementDiscountCookie: clearEntitlementDiscountCookie,
    refreshEntitlementCartDrawer: refreshEntitlementCartDrawer,
    reconcileEntitlement: reconcileEntitlement,
    createEntitlementScheduler: createEntitlementScheduler,
  };
}
